import React, { useState, useEffect } from 'react';
import { Button } from '@/components/shadcn-ui/button';
import { Input } from '@/components/shadcn-ui/input';
import { Label } from '@/components/shadcn-ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/shadcn-ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn-ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn-ui/tabs';
import { DataTable } from '@/components/DataTable';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Settings } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { Badge } from '@/components/shadcn-ui/badge';
import { Switch } from '@/components/shadcn-ui/switch';

const IpManagement = () => {
    const [activeTab, setActiveTab] = useState('ip-management');

    // IP Management State
    const [ipManagements, setIpManagements] = useState([]);
    const [ipLoading, setIpLoading] = useState(false);
    const [ipModalOpen, setIpModalOpen] = useState(false);
    const [ipFormData, setIpFormData] = useState({
        name: '',
        status: 'active',
        vpn_user: '',
        vpn_password: '',
        vpn_server: ''
    });
    const [editingIp, setEditingIp] = useState(null);

    // County Management State
    const [ipRotations, setIpRotations] = useState([]);
    const [countyLoading, setCountyLoading] = useState(false);
    const [countyModalOpen, setCountyModalOpen] = useState(false);
    const [countyFormData, setCountyFormData] = useState({
        county: '',
        proxy_required: false,
        system_ip_allowed: false
    });
    const [editingCounty, setEditingCounty] = useState(null);

    // Proxy Management State
    const [proxyModalOpen, setProxyModalOpen] = useState(false);
    const [selectedCounty, setSelectedCounty] = useState(null);
    const [availableIpManagements, setAvailableIpManagements] = useState([]);
    const [selectedIpManagement, setSelectedIpManagement] = useState('');

    // Fetch IP Managements
    const fetchIpManagements = async () => {
        setIpLoading(true);
        try {
            const response = await appAxios.get('/Admin/v1.0/get-ip-managements');
            setIpManagements(response.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch IP managements');
        } finally {
            setIpLoading(false);
        }
    };

    // Fetch IP Rotations
    const fetchIpRotations = async () => {
        setCountyLoading(true);
        try {
            const response = await appAxios.get('/Admin/v1.0/get-ip-rotations');
            setIpRotations(response.data.data || []);
        } catch (error) {
            toast.error('Failed to fetch IP rotations');
        } finally {
            setCountyLoading(false);
        }
    };

    useEffect(() => {
        fetchIpManagements();
        fetchIpRotations();
    }, []);

    // IP Management Handlers
    const handleIpSubmit = async () => {
        try {
            if (editingIp) {
                await appAxios.post(`/Admin/v1.0/update-ip-management-status/${editingIp._id}`, {
                    status: ipFormData.status
                });
                toast.success('IP Management updated successfully');
            } else {
                await appAxios.post('/Admin/v1.0/create-ip-management', ipFormData);
                toast.success('IP Management created successfully');
            }
            setIpModalOpen(false);
            resetIpForm();
            fetchIpManagements();
            fetchIpRotations();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save IP management');
        }
    };

    const resetIpForm = () => {
        setIpFormData({
            name: '',
            status: 'active',
            vpn_user: '',
            vpn_password: '',
            vpn_server: ''
        });
        setEditingIp(null);
    };

    const openEditIpModal = (ip) => {
        setEditingIp(ip);
        setIpFormData({
            name: ip.name,
            status: ip.status,
            vpn_user: ip.vpn_user,
            vpn_password: '',
            vpn_server: ip.vpn_server
        });
        setIpModalOpen(true);
    };

    // County Management Handlers
    const handleCountySubmit = async () => {
        try {
            if (editingCounty) {
                const data = {
                    ipRotationId: editingCounty._id,
                    proxyRequired: countyFormData.proxy_required,
                    systemIpAllowed: !countyFormData.proxy_required
                };
                await appAxios.post('/Admin/v1.0/update-ip-rotation', data);
                toast.success('County configuration updated successfully');
            } else {
                const data = {
                    county: countyFormData.county,
                    proxyRequired: countyFormData.proxy_required,
                    systemIpAllowed: !countyFormData.proxy_required
                };
                await appAxios.post('/Admin/v1.0/create-ip-rotation', data);
                toast.success('County configuration created successfully');
            }
            setCountyModalOpen(false);
            resetCountyForm();
            fetchIpRotations();
        } catch (error) {
            toast.error(error.response?.data?.message || `Failed to ${editingCounty ? 'update' : 'create'} county configuration`);
        }
    };

    const resetCountyForm = () => {
        setCountyFormData({
            county: '',
            proxy_required: false,
            system_ip_allowed: false
        });
        setEditingCounty(null);
    };

    const openEditCountyModal = (county) => {
        setEditingCounty(county);
        setCountyFormData({
            county: county.county,
            proxy_required: county.proxy_required,
            system_ip_allowed: county.system_ip_allowed
        });
        setCountyModalOpen(true);
    };

    // Proxy Management Handlers
    const openProxyModal = async (county) => {
        setSelectedCounty(county);

        // Get available IP managements (not already in this county)
        const usedIpIds = county.available_proxies?.map(p => p.ip_management_id._id) || [];
        const available = ipManagements.filter(ip => !usedIpIds.includes(ip._id));
        setAvailableIpManagements(available);

        setProxyModalOpen(true);
    };

    const handleAddProxy = async () => {
        if (!selectedIpManagement) {
            toast.error('Please select an IP management');
            return;
        }

        try {
            await appAxios.post('/Admin/v1.0/assign-ip-management-to-ip-rotation', {
                ipRotationId: selectedCounty._id,
                ipManagementId: selectedIpManagement
            });
            toast.success('Proxy added successfully');
            setProxyModalOpen(false);
            setSelectedIpManagement('');
            fetchIpRotations();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to add proxy');
        }
    };

    const handleRemoveProxy = async (county, proxyId) => {
        try {
            await appAxios.post('/Admin/v1.0/remove-ip-management-to-ip-rotation', {
                ipRotationId: county,
                ipManagementId: proxyId
            });
            toast.success('Proxy removed successfully');
            setProxyModalOpen(false);
            fetchIpRotations();
            fetchIpManagements();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to remove proxy');
        }
    };

    const handleProxyStatusToggle = async (ipManagementId, ipRotationId, currentStatus) => {
        try {
            const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
            await appAxios.post(`/Admin/v1.0/update-ip-rotation-status`, {
                status: newStatus,
                ipRotationId: ipRotationId,
                ipManagementId: ipManagementId,
            });
            toast.success('Proxy status updated');
            setProxyModalOpen(false);
            fetchIpRotations();
            fetchIpManagements();
        } catch (error) {
            toast.error('Failed to update proxy status');
        }
    };

    // IP Management Columns
    const ipColumns = [
        {
            key: 'name',
            label: 'Name',
            cellClassName: 'font-medium'
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => (
                <Badge variant={value === 'active' ? 'success' : value === 'inactive' ? 'warning' : 'destructive'}>
                    {value}
                </Badge>
            )
        },
        {
            key: 'vpn_user',
            label: 'VPN User',
            render: (value) => (
                <span>{value.slice(0, 8)}***</span>
            )
        },
        {
            key: 'vpn_server',
            label: 'VPN Server'
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_, row) => (
                <Button variant="ghost" size="sm" onClick={() => openEditIpModal(row)}>
                    <Edit className="h-4 w-4" />
                </Button>
            )
        }
    ];

    // County Management Columns
    const countyColumns = [
        {
            key: 'county',
            label: 'County',
            cellClassName: 'font-medium capitalize'
        },
        {
            key: 'proxy_required',
            label: 'Proxy Required',
            render: (value) => (
                <Badge variant={value ? 'success' : 'warning'}>
                    {value ? 'Yes' : 'No'}
                </Badge>
            )
        },
        {
            key: 'system_ip_allowed',
            label: 'System IP Allowed',
            render: (value) => (
                <Badge variant={value ? 'success' : 'warning'}>
                    {value ? 'Yes' : 'No'}
                </Badge>
            )
        },
        {
            key: 'available_proxies',
            label: 'Proxies',
            render: (value) => (
                <span>{value?.length || 0} configured</span>
            )
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_, row) => (
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEditCountyModal(row)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Configuration
                    </Button>
                    <Button variant="outline" size="sm" disabled={!row.proxy_required} onClick={() => openProxyModal(row)}>
                        <Settings className="h-4 w-4 mr-2" />
                        Manage Proxies
                    </Button>
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold tracking-tight">IP Management</h1>
                <p className="text-muted-foreground">Manage IP addresses and county configurations</p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="ip-management">IP Management</TabsTrigger>
                    <TabsTrigger value="county-management">County Management</TabsTrigger>
                </TabsList>

                <TabsContent value="ip-management" className="space-y-4">
                    <div className="flex justify-end">
                        <Button onClick={() => { resetIpForm(); setIpModalOpen(true); }}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add IP Management
                        </Button>
                    </div>

                    <DataTable
                        data={ipManagements}
                        columns={ipColumns}
                        loading={ipLoading}
                        emptyMessage="No IP managements found"
                    />
                </TabsContent>

                <TabsContent value="county-management" className="space-y-4">
                    <div className="flex justify-end">
                        <Button onClick={() => { resetCountyForm(); setCountyModalOpen(true); }}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add County Configuration
                        </Button>
                    </div>

                    <DataTable
                        data={ipRotations}
                        columns={countyColumns}
                        loading={countyLoading}
                        emptyMessage="No county configurations found"
                    />
                </TabsContent>
            </Tabs>

            {/* IP Management Modal */}
            <Dialog open={ipModalOpen} onOpenChange={setIpModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingIp ? 'Edit IP Management' : 'Add IP Management'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Name</Label>
                            <Input
                                value={ipFormData.name}
                                onChange={(e) => setIpFormData({ ...ipFormData, name: e.target.value })}
                                placeholder="Enter name"
                                disabled={editingIp}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Status</Label>
                            <Select value={ipFormData.status} onValueChange={(value) => setIpFormData({ ...ipFormData, status: value })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="inactive">Inactive</SelectItem>
                                    <SelectItem value="blocked">Blocked</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {!editingIp && (
                            <>
                                <div className="space-y-2">
                                    <Label>VPN User</Label>
                                    <Input
                                        value={ipFormData.vpn_user}
                                        onChange={(e) => setIpFormData({ ...ipFormData, vpn_user: e.target.value })}
                                        placeholder="Enter VPN user"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>VPN Password</Label>
                                    <Input
                                        type="password"
                                        value={ipFormData.vpn_password}
                                        onChange={(e) => setIpFormData({ ...ipFormData, vpn_password: e.target.value })}
                                        placeholder="Enter VPN password"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>VPN Server</Label>
                                    <Input
                                        value={ipFormData.vpn_server}
                                        onChange={(e) => setIpFormData({ ...ipFormData, vpn_server: e.target.value })}
                                        placeholder="Enter VPN server"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIpModalOpen(false)}>Cancel</Button>
                        <Button onClick={handleIpSubmit}>
                            {editingIp ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* County Configuration Modal */}
            <Dialog open={countyModalOpen} onOpenChange={setCountyModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingCounty ? 'Edit County Configuration' : 'Add County Configuration'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>County</Label>
                            <Select
                                value={countyFormData.county}
                                onValueChange={(value) => setCountyFormData({ ...countyFormData, county: value })}
                                disabled={editingCounty}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select county" />
                                </SelectTrigger>
                                <SelectContent>
                                    {(() => {
                                        const availableCounties = ['ladbs', 'folsom', 'sacramento']
                                            .filter(county =>
                                                editingCounty ||
                                                !ipRotations.some(rotation => rotation.county.toLowerCase() === county)
                                            );

                                        if (availableCounties.length === 0) {
                                            return (
                                                <div className="p-2 text-sm text-muted-foreground text-center">
                                                    No counties available
                                                </div>
                                            );
                                        }

                                        return availableCounties.map(county => (
                                            <SelectItem key={county} value={county}>
                                                {county.charAt(0).toUpperCase() + county.slice(1)}
                                            </SelectItem>
                                        ));
                                    })()}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center justify-between">
                            <Label>Proxy Required</Label>
                            <Switch
                                checked={countyFormData.proxy_required}
                                onCheckedChange={(checked) => setCountyFormData({ ...countyFormData, proxy_required: checked })}
                            />
                        </div>
                    </div>
                    <DialogFooter className="border-t pt-4">
                        <Button variant="outline" onClick={() => setCountyModalOpen(false)}>Cancel</Button>
                        <Button onClick={handleCountySubmit}>
                            {editingCounty ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Proxy Management Modal */}
            <Dialog open={proxyModalOpen} onOpenChange={setProxyModalOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Manage Proxies - {selectedCounty?.county?.toLowerCase()}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Add Proxy Section */}
                        <div className="flex gap-2">
                            <Select value={selectedIpManagement} onValueChange={setSelectedIpManagement}>
                                <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="Select IP Management" />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableIpManagements.length === 0 ? (
                                        <div className="p-2 text-sm text-muted-foreground text-center">
                                            No IP managements available
                                        </div>
                                    ) : (
                                        availableIpManagements.map(ip => (
                                            <SelectItem key={ip._id} value={ip._id}>
                                                {ip.name} ({ip.vpn_server})
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                            <Button onClick={handleAddProxy}>
                                <Plus className="h-4 w-4 mr-2" />
                                Add
                            </Button>
                        </div>

                        {/* Configured Proxies */}
                        <div className="space-y-2">
                            <Label>Configured Proxies</Label>
                            <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                                {selectedCounty?.available_proxies?.length === 0 ? (
                                    <div className="p-4 text-center text-muted-foreground">
                                        No proxies configured
                                    </div>
                                ) : (
                                    selectedCounty?.available_proxies?.map(proxy => (
                                        <div key={proxy._id} className="p-3 flex items-center justify-between">
                                            <div className="flex-1">
                                                <div className="font-medium">{proxy.ip_management_id?.name}</div>

                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge variant={proxy.status === 'active' ? 'success' : 'warning'}>
                                                    {proxy.status}
                                                </Badge>
                                                <Switch
                                                    checked={proxy.status === 'active'}
                                                    onCheckedChange={() => handleProxyStatusToggle(proxy.ip_management_id?._id, selectedCounty._id, proxy.status)}
                                                />
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleRemoveProxy(selectedCounty._id, proxy.ip_management_id?._id)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setProxyModalOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default IpManagement;
